/**
 * Collection list layout: inline variant pickers + per-variant quantity controls.
 * Loaded via boaideas-assets on collection pages only.
 */
(() => {
  const SECTION_ID = 'boa-collection-variant-card';

  class BoaCollectionProductCard {
    constructor(card) {
      this.card = card;
      this.images = card.querySelectorAll('.card__main-image');
      this.links = card.querySelectorAll('.js-prod-link');
      this.quickAddBtn = card.querySelector('.js-quick-add');
      this.carouselSlider = card.querySelector('product-card-image-slider');
      this.cardVariantPicker = card.querySelector('[data-card-variant-picker]');
      this.listProductForm = card.querySelector('.card__list-product-form');
      this.cartQuantities = window.boaCollectionVariants?.cartQuantities || {};

      if (this.quickAddBtn) {
        this.productUrl = this.quickAddBtn.dataset.productUrl;
      } else if (this.links.length) {
        this.productUrl = this.links[0].href;
      }

      this.boundHandleDocumentLineItemChange = this.handleDocumentLineItemChange.bind(this);
    }

    init() {
      if (!this.cardVariantPicker || !this.listProductForm) {
        return;
      }

      this.variantsData = this.buildVariantsData();

      this.cardVariantPicker.addEventListener('change', this.handleCardVariantChange.bind(this));
      this.cardVariantPicker.addEventListener('click', (evt) => {
        evt.stopPropagation();
      });

      this.listProductForm.addEventListener('on:cart:add', this.handleCardCartAdd.bind(this));
      document.addEventListener('on:line-item:change', this.boundHandleDocumentLineItemChange);

      const quantityInput = this.listProductForm.querySelector('.qty-input__input');
      if (quantityInput) {
        quantityInput.addEventListener('change', this.handleCardQuantityInputChange.bind(this));
      }

      this.updateCardQuantityState(this.listProductForm.dataset.variantId);
    }

    destroy() {
      document.removeEventListener('on:line-item:change', this.boundHandleDocumentLineItemChange);
    }

    buildVariantsData() {
      const rawVariants = JSON.parse(
        this.cardVariantPicker.querySelector('.js-boa-card-variants-data').textContent
      );

      return rawVariants.map((variant) => ({
        id: variant.id,
        available: variant.available,
        options: [variant.option1, variant.option2, variant.option3].filter(Boolean)
      }));
    }

    static getCardVariantKey(variantId) {
      return variantId.toString();
    }

    getCardCartQuantity(variantId) {
      return Number(this.cartQuantities[BoaCollectionProductCard.getCardVariantKey(variantId)] || 0);
    }

    setCardCartQuantity(variantId, quantity) {
      this.cartQuantities[BoaCollectionProductCard.getCardVariantKey(variantId)] = quantity;
    }

    hasCardVariant(variantId) {
      return this.variantsData.some((variant) => variant.id.toString() === variantId.toString());
    }

    handleCardVariantChange(evt) {
      const selectedOptions = [];
      this.cardVariantPicker.querySelectorAll('.js-card-option').forEach((input) => {
        if (input.checked) {
          selectedOptions[Number(input.dataset.optionPosition) - 1] = input.value;
        }
      });

      const variant = this.variantsData.find((v) => v.options.every(
        (option, index) => option === selectedOptions[index]
      ));

      if (!variant) {
        return;
      }

      const variantInput = this.listProductForm.querySelector('.js-card-variant-id');
      if (variantInput) {
        variantInput.value = variant.id;
      }

      this.listProductForm.dataset.variantId = variant.id;

      const atcBtn = this.listProductForm.querySelector('[name="add"]');
      if (atcBtn) {
        atcBtn.disabled = !variant.available;
      }

      this.updateCardVariantLinks(variant.id);
      this.updateCardVariantImage(evt?.target);
      this.updateCardQuantityState(variant.id);
    }

    updateCardVariantLinks(variantId) {
      const url = new URL(this.productUrl || this.links[0].href, window.location.origin);
      url.searchParams.set('variant', variantId);

      this.links.forEach((link) => {
        link.href = url.toString();
      });
    }

    updateCardVariantImage(optionInput) {
      if (!optionInput?.dataset?.mediaId || this.carouselSlider) {
        return;
      }

      const variantMedia = this.card.querySelector(`[data-media-id="${optionInput.dataset.mediaId}"]`);
      if (!variantMedia) {
        // The card only renders the product's featured image, so swap its source to the variant's image.
        const mainImage = this.images[0] || this.card.querySelector('img');
        if (mainImage && optionInput.dataset.mediaSrc) {
          mainImage.removeAttribute('srcset');
          mainImage.removeAttribute('sizes');
          mainImage.src = optionInput.dataset.mediaSrc;
          this.images.forEach((image) => { image.hidden = image !== mainImage; });
        }
        return;
      }

      this.images.forEach((image) => { image.hidden = true; });
      variantMedia.hidden = false;
    }

    updateCardQuantityState(variantId) {
      const quantitySelector = this.listProductForm.querySelector('quantity-input');
      if (!quantitySelector) {
        return;
      }

      const quantityInput = quantitySelector.querySelector('.qty-input__input');
      const cartQuantity = this.getCardCartQuantity(variantId);

      quantityInput.value = cartQuantity;
      quantityInput.dataset.initialValue = cartQuantity;
      quantityInput.dataset.variantId = variantId;
      quantitySelector.currentQty = cartQuantity;
      this.listProductForm.setAttribute(
        'data-show-quantity-selector',
        cartQuantity > 0 ? 'true' : 'false'
      );
    }

    handleCardQuantityInputChange(evt) {
      const quantity = parseInt(evt.target.value, 10) || 0;
      this.setCardCartQuantity(this.listProductForm.dataset.variantId, quantity);
    }

    handleDocumentLineItemChange(evt) {
      if (!this.hasCardVariant(evt.detail.variantId)) {
        return;
      }

      this.setCardCartQuantity(evt.detail.variantId, evt.detail.newQuantity);

      if (evt.detail.variantId.toString() === this.listProductForm?.dataset.variantId) {
        this.updateCardQuantityState(evt.detail.variantId);
      }
    }

    handleCardCartAdd(evt) {
      const variantId = evt.detail.variantId;
      const currentQuantity = this.getCardCartQuantity(variantId);
      this.setCardCartQuantity(variantId, currentQuantity > 0 ? currentQuantity : 1);

      if (variantId.toString() === this.listProductForm.dataset.variantId) {
        this.updateCardQuantityState(variantId);
      }
    }
  }

  class BoaCollectionVariants {
    constructor(options = {}) {
      this.productsGrid = options.productsGrid || document.querySelector('.main-products-grid');
      this.mount = options.mount || document.getElementById('boa-collection-variants-mount');
      this.cartQuantities = options.cartQuantities || null;
      this.cardControllers = new Map();
      this.pendingHandles = new Set();
    }

    init() {
      if (!this.productsGrid || !this.mount) {
        return;
      }

      if (!this.cartQuantities) {
        const cartQtyEl = document.getElementById('boa-cart-quantities');
        this.cartQuantities = cartQtyEl ? JSON.parse(cartQtyEl.textContent) : {};
      }

      window.boaCollectionVariants = window.boaCollectionVariants || this;
      window.boaCollectionVariants.cartQuantities = this.cartQuantities;

      this.productsGrid.dataset.layout = 'list';
      this.initCards();
      this.observeNewCards();
    }

    initCards() {
      this.productsGrid.querySelectorAll('product-card[data-product-id]').forEach((card) => {
        this.setupCard(card);
      });
    }

    observeNewCards() {
      const results = this.productsGrid.querySelector('.main-products-grid__results');
      if (!results || !('MutationObserver' in window)) {
        return;
      }

      this.observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType !== Node.ELEMENT_NODE) {
              return;
            }

            node.querySelectorAll?.('product-card[data-product-id]').forEach((card) => {
              this.setupCard(card);
            });

            if (node.matches?.('product-card[data-product-id]')) {
              this.setupCard(node);
            }
          });
        });
      });

      this.observer.observe(results, { childList: true, subtree: true });
    }

    getProductHandle(card) {
      const quickAddBtn = card.querySelector('.js-quick-add');
      if (!quickAddBtn?.dataset.productUrl) {
        return null;
      }

      const path = new URL(quickAddBtn.dataset.productUrl, window.location.origin).pathname;
      const parts = path.split('/').filter(Boolean);
      return parts[parts.length - 1] || null;
    }

    async fetchVariantMount(handle) {
      if (this.pendingHandles.has(handle)) {
        return null;
      }
      this.pendingHandles.add(handle);

      try {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}products/${handle}?sections=${SECTION_ID}`);
        if (!response.ok) {
          return null;
        }

        const data = await response.json();
        const html = data[SECTION_ID];
        if (!html) {
          return null;
        }

        const template = document.createElement('template');
        template.innerHTML = html;
        return template.content.querySelector('[data-boa-product-id]');
      } catch (error) {
        console.log(error); // eslint-disable-line
        return null;
      } finally {
        this.pendingHandles.delete(handle);
      }
    }

    getMountForCard(card) {
      const productId = card.dataset.productId;
      const blockId = this.productsGrid.dataset.boaBlockId;

      if (blockId) {
        return this.mount.querySelector(
          `[data-boa-block-id="${blockId}"][data-boa-product-id="${productId}"]`
        );
      }

      return this.mount.querySelector(`[data-boa-product-id="${productId}"]`);
    }

    async setupCard(card) {
      if (card.dataset.boaVariantsReady === 'true') {
        return;
      }

      const quickAddBtn = card.querySelector('.js-quick-add');
      if (!quickAddBtn || quickAddBtn.dataset.productDefaultVariant === 'true') {
        return;
      }

      let mountEntry = this.getMountForCard(card);
      let pickerSource = mountEntry?.querySelector('[data-boa-variant-picker]');
      let formSource = mountEntry?.querySelector('[data-boa-list-form]');

      if (!pickerSource?.firstElementChild || !formSource?.firstElementChild) {
        const handle = this.getProductHandle(card);
        if (!handle) {
          return;
        }

        // Claim the card before the first await so concurrent setupCard calls bail out.
        card.dataset.boaVariantsReady = 'true';
        mountEntry = await this.fetchVariantMount(handle);

        if (mountEntry) {
          this.mount.appendChild(mountEntry);
        }

        pickerSource = mountEntry?.querySelector('[data-boa-variant-picker]');
        formSource = mountEntry?.querySelector('[data-boa-list-form]');

        if (!pickerSource?.firstElementChild || !formSource?.firstElementChild) {
          delete card.dataset.boaVariantsReady;
          return;
        }
      } else {
        card.dataset.boaVariantsReady = 'true';
      }

      if (!pickerSource?.firstElementChild || !formSource?.firstElementChild) {
        delete card.dataset.boaVariantsReady;
        return;
      }

      const infoInner = card.querySelector('.card__info-inner');
      const title = card.querySelector('.card__title');
      if (infoInner && title && !card.querySelector('[data-card-variant-picker]')) {
        const pickerWrap = document.createElement('div');
        pickerWrap.className = 'card__list-variant-picker boa-card__list-variant-picker no-js-hidden';
        pickerWrap.appendChild(pickerSource.firstElementChild);
        title.insertAdjacentElement('afterend', pickerWrap);
      }

      const quickAdd = card.querySelector('.card__quick-add');
      const quickAddContent = quickAdd?.querySelector(':scope > .no-js-hidden');
      if (quickAdd && quickAddContent && !quickAdd.querySelector('.card__quick-add-grid')) {
        const gridWrap = document.createElement('div');
        gridWrap.className = 'card__quick-add-grid no-js-hidden';
        gridWrap.appendChild(quickAddContent);
        quickAdd.prepend(gridWrap);
      }

      if (quickAdd && !card.querySelector('.card__list-product-form')) {
        quickAdd.appendChild(formSource.firstElementChild);
      }

      card.classList.add('card--has-list-variants');

      const controller = new BoaCollectionProductCard(card);
      controller.init();
      this.cardControllers.set(card, controller);
    }
  }

  const getSharedCartQuantities = () => {
    const cartQtyEl = document.getElementById('boa-cart-quantities');
    return cartQtyEl ? JSON.parse(cartQtyEl.textContent) : {};
  };

  const initGrid = (grid, mount, cartQuantities) => {
    if (!grid || !mount || grid.dataset.boaVariantsInit === 'true') {
      return;
    }

    grid.dataset.boaVariantsInit = 'true';
    new BoaCollectionVariants({
      productsGrid: grid,
      mount,
      cartQuantities
    }).init();
  };

  const init = (scope = document) => {
    const cartQuantities = getSharedCartQuantities();
    const roots = [];

    if (scope.matches?.('[data-product-catalog]')) {
      roots.push(scope);
    }

    scope.querySelectorAll?.('[data-product-catalog]').forEach((root) => {
      roots.push(root);
    });

    roots.forEach((root) => {
      const mount = root.querySelector('[data-boa-variants-mount]');
      if (!mount) {
        return;
      }

      root.querySelectorAll('[data-boa-products-grid]').forEach((grid) => {
        initGrid(grid, mount, cartQuantities);
      });
    });

    const defaultMount = document.getElementById('boa-collection-variants-mount');
    const defaultGrid = document.querySelector('.main-products-grid:not([data-boa-products-grid])');
    if (defaultMount && defaultGrid) {
      initGrid(defaultGrid, defaultMount, cartQuantities);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => {
    init(event.target);
  });
})();
